import { Button, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { PlusIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { getYardState, type YardState } from "../staff/yard-state";
import { AnnuaireLink, YardScreenHeader } from "./components";
import { ContractGroupForm } from "./contract-group-form";
import { ContractInterruptDialog } from "./contract-interrupt-dialog";
import { ContractTeamsDrawer } from "./contract-teams-drawer";
import {
  type ContractGroup,
  ContractGroupsToolbar,
  ContractsSlideOver,
  contractsGridSectionClass,
  ReferentHistoryDeleteDialog,
  type ReferentHistoryRemoval,
} from "./contracts-panel";
import { DeactivateCollaboratorModal } from "./deactivate-collaborator-modal";
import {
  InterventionMatrix,
  type MatrixContract,
  type MatrixGroup,
  type MatrixTeam,
  ToolbarButton,
} from "./intervention-matrix-grid";
import { PerformancesDrawer } from "./performances-drawer";
import { WorkContractReferentForm } from "./work-contract-referent-form";
import {
  employeeName,
  type WorkContractRow,
  WorkContractsEditablePanel,
} from "./work-contracts-editable-panel";

type DeleteTarget =
  | {
      kind: "contract";
      id: number;
      label: string;
      /** Rang 1: its rang 2 contracts are deleted with it. */
      subContractCount: number;
    }
  | { kind: "group"; id: number; label: string }
  | {
      kind: "referent";
      contractId: number;
      employeeContractId: number;
      label: string;
    };

export function YardTravauxPage() {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();
  const numericYardId = Number(yardId);

  const [yardName, setYardName] = useState<string | null>(null);
  const [yardState, setYardState] = useState<YardState | null>(null);
  const [isConceptionRealisation, setIsConceptionRealisation] = useState(false);
  const [yardMoaCompany, setYardMoaCompany] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [contracts, setContracts] = useState<WorkContractRow[]>([]);
  const [groups, setGroups] = useState<ContractGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Group creation form; existing groups are edited in the contracts panel's draft
  const [groupFormOpen, setGroupFormOpen] = useState(false);

  // Referent form state
  const [referentFormOpen, setReferentFormOpen] = useState(false);
  const [referentContract, setReferentContract] =
    useState<WorkContractRow | null>(null);
  // Referent being deactivated on their contract (successors asked first)
  const [deactivatingReferent, setDeactivatingReferent] =
    useState<WorkContractRow | null>(null);

  // Teams drawer state (kept by id so it reflects refreshed data)
  const [teamsContractId, setTeamsContractId] = useState<number | null>(null);

  // Prestations drawer + matrix refresh key (reloads the matrix on changes)
  const [prestasDrawerOpen, setPrestasDrawerOpen] = useState(false);
  const [matrixRefreshKey, setMatrixRefreshKey] = useState(0);
  const [matrixDraftActions, setMatrixDraftActions] =
    useState<HTMLDivElement | null>(null);

  // Groupements collapsed by default (la grille interventions doit occuper
  // un maximum d'espace sur l'écran).
  const [groupsExpanded, setGroupsExpanded] = useState(false);

  // Collapsible contracts panel, open by default so the matrix can be given
  // full width when it needs more room for many zones.
  const [contractsPanelOpen, setContractsPanelOpen] = useState(true);
  const [isContractsDirty, setIsContractsDirty] = useState(false);

  // Former referent whose « x » was clicked (removal confirmed first)
  const [historyRemoval, setHistoryRemoval] =
    useState<ReferentHistoryRemoval | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Interrupt state — société défaillante en cours de chantier, with
  // the takeover of its interventions.
  const [interruptTarget, setInterruptTarget] =
    useState<WorkContractRow | null>(null);

  const fetchYard = useCallback(async () => {
    const { data } = await apiClient.GET("/api/admin/yards/{id}", {
      params: { path: { id: numericYardId } },
    });
    if (data) {
      const found = data.data.yard;
      setYardName(found.name);
      setYardState(getYardState(found));
      setIsConceptionRealisation(found.type === "conception_realisation");
      setYardMoaCompany(found.moaCompany);
    }
  }, [numericYardId]);

  const fetchData = useCallback(async () => {
    const { data, error } = await apiClient.GET(
      "/api/admin/yards/work-contracts",
      { params: { query: { yardId: numericYardId } } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Échec du chargement des contrats travaux",
      });
      setLoading(false);
      return;
    }
    setContracts(
      data.data.contracts.map((c) => ({
        id: c.id,
        type: c.type,
        shortName: c.shortName,
        isActive: c.isActive,
        groupId: c.groupId,
        customerId: c.customer?.id ?? null,
        parentContractId: c.parentContractId,
        hasInterventions: c.hasInterventions,
        performer: c.performer,
        customer: c.customer,
        group: c.group,
        referent: c.referent,
        referentHistory: c.referentHistory,
        teams: c.teams.map((t) => ({
          id: t.id,
          name: t.name,
          delaiPourVisaDays: t.delaiPourVisaDays,
          referentHistory: t.referentHistory,
          members: t.members,
        })),
      })),
    );
    setGroups(data.data.groups);
    setLoading(false);
    // The matrix loads its own interventions, which a contract change can touch.
    setMatrixRefreshKey((k) => k + 1);
  }, [numericYardId]);

  useEffect(() => {
    fetchYard();
    fetchData();
  }, [fetchYard, fetchData]);

  const isCommissioned = isNotNil(yardState) && yardState !== "preparation";
  const canAddContracts =
    yardState === "preparation" || yardState === "en_service";

  // Rang 2 contracts sub-contracted from the given rang 1 contract.
  const subContractCount = (contractId: number) =>
    contracts.filter((c) => c.parentContractId === contractId).length;

  // Inputs for the intervention matrix editor. Interrupted contracts
  // can no longer be picked for new assignments — only active ones show up.
  const activeContracts = contracts.filter((c) => c.isActive);
  const matrixContracts: MatrixContract[] = activeContracts.map((c) => ({
    id: c.id,
    label: isNotNil(c.shortName)
      ? `${c.performer.name} (${c.shortName})`
      : c.performer.name,
    shortName: c.shortName,
    type: c.type,
    groupId: c.groupId,
    parentContractId: c.parentContractId,
  }));
  const matrixTeams: MatrixTeam[] = activeContracts.flatMap((c) =>
    c.teams.map((team) => ({
      id: team.id,
      name: team.name,
      rang: (c.type === "travaux_n_1" ? 1 : 2) as 1 | 2,
      contractId: c.id,
    })),
  );
  // Every team, including those under an interrupted contract: needed so
  // already-assigned team names still render on cells awaiting reprise
  // (only `matrixTeams` above is restricted, to keep them out of pickers).
  const matrixAllTeams: MatrixTeam[] = contracts.flatMap((c) =>
    c.teams.map((team) => ({
      id: team.id,
      name: team.name,
      rang: (c.type === "travaux_n_1" ? 1 : 2) as 1 | 2,
      contractId: c.id,
    })),
  );
  // Every contract, including interrupted ones — mirrors matrixAllTeams, so
  // an already-assigned cell can still resolve its abbreviation for display.
  const matrixAllContracts: MatrixContract[] = contracts.map((c) => ({
    id: c.id,
    label: isNotNil(c.shortName)
      ? `${c.performer.name} (${c.shortName})`
      : c.performer.name,
    shortName: c.shortName,
    type: c.type,
    groupId: c.groupId,
    parentContractId: c.parentContractId,
  }));
  const matrixGroups: MatrixGroup[] = groups.map((g) => ({
    id: g.id,
    mandataireContractId: g.mandataireContractId,
  }));

  const openReferentForm = (contract: WorkContractRow) => {
    setReferentContract(contract);
    setReferentFormOpen(true);
  };

  const confirmDelete = async () => {
    if (isNil(deleteTarget)) return;
    setIsDeleting(true);

    let apiError: { error?: string } | undefined;
    if (deleteTarget.kind === "contract") {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/contracts/{id}",
        { params: { path: { id: deleteTarget.id } } },
      ));
    } else if (deleteTarget.kind === "group") {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/yards/contract-groups/{id}",
        { params: { path: { id: deleteTarget.id } } },
      ));
    } else {
      ({ error: apiError } = await apiClient.DELETE(
        "/api/admin/yards/work-contracts/{contractId}/referent/{employeeContractId}",
        {
          params: {
            path: {
              contractId: deleteTarget.contractId,
              employeeContractId: deleteTarget.employeeContractId,
            },
          },
        },
      ));
    }

    setIsDeleting(false);
    setDeleteTarget(null);
    if (isNotNil(apiError)) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression",
      });
      return;
    }
    toaster.success({ title: "Succès", description: "Suppression effectuée" });
    fetchData();
  };

  const onInterrupted = () => {
    setInterruptTarget(null);
    fetchData();
  };

  if (loading) {
    return (
      <div className="container mx-auto flex h-full items-center justify-center p-6 text-gray-500">
        Chargement...
      </div>
    );
  }

  return (
    // Bounded to the height AdminLayout hands down (screen-design "no
    // page-level scroll" rule): the header is shrink-0 (fixed), the
    // matrix/contracts row below gets all the remaining space, and within it
    // only the matrix grid and the contracts panel scroll their own content
    // — never the page itself, so sticky headers/columns stay put.
    <div className="container mx-auto flex h-full min-h-0 flex-col overflow-hidden p-6">
      <YardScreenHeader
        yardName={yardName}
        yardState={yardState}
        screenTitle="Contrats et équipes travaux"
        onBack={() => navigate(`/admin/yards/${numericYardId}`)}
        backLabel="Retour au menu de paramétrage"
        actions={
          <>
            <div ref={setMatrixDraftActions} className="ml-auto" />
            <AnnuaireLink />
          </>
        }
      />

      {/* Matrice + panneau contrats travaux : le panneau glisse en overlay
          par-dessus la matrice (il ne redimensionne jamais sa largeur). Prend
          tout l'espace restant sous le header fixe ; la matrice gère
          elle-même son propre scroll interne (Prestation/zones figées), et
          l'overlay (inset-y-0) suit cette même hauteur réelle. */}
      <div className="relative flex min-h-0 flex-1 items-start gap-6 overflow-hidden">
        <section className={contractsGridSectionClass}>
          <div className="min-h-0 flex-1">
            <InterventionMatrix
              refreshKey={matrixRefreshKey}
              yardId={numericYardId}
              contracts={matrixContracts}
              teams={matrixTeams}
              allTeams={matrixAllTeams}
              allContracts={matrixAllContracts}
              groups={matrixGroups}
              contractsLocked={
                isNotNil(yardState) && yardState !== "preparation"
              }
              draftActionsTarget={matrixDraftActions}
              headerContent={
                <>
                  <h3
                    className="shrink-0 whitespace-nowrap text-sm font-semibold text-zinc-900 dark:text-zinc-100"
                    title="Prestations (lignes) × zones (colonnes). Choisissez une pré-sélection puis cochez les cases, ou cliquez une cellule pour en détailler les contrats et les équipes."
                  >
                    Périmètre des interventions
                  </h3>
                  <ToolbarButton
                    title="Gérer les prestations des lots"
                    icon={PlusIcon}
                    onClick={() => setPrestasDrawerOpen(true)}
                  >
                    Gérer prestas
                  </ToolbarButton>
                </>
              }
            />
          </div>
        </section>

        <ContractsSlideOver
          open={contractsPanelOpen}
          onOpenChange={setContractsPanelOpen}
          isDirty={isContractsDirty}
        >
          <WorkContractsEditablePanel
            yardId={numericYardId}
            contracts={contracts}
            groups={groups}
            groupsExpanded={groupsExpanded}
            groupsNote={
              isConceptionRealisation
                ? undefined
                : "Hors conception-réalisation, un groupement ne peut pas mêler entreprises de travaux et donneurs d'ordre : les groupements de donneurs d'ordre (écran 909) ne sont pas proposés ici."
            }
            onDeleteGroup={(group: ContractGroup) =>
              setDeleteTarget({
                kind: "group",
                id: group.id,
                label: `le groupement ${group.name}`,
              })
            }
            yardMoaCompany={yardMoaCompany}
            isCommissioned={isCommissioned}
            onDirtyChange={setIsContractsDirty}
            onSuccess={fetchData}
            onManageTeams={(contract) => setTeamsContractId(contract.id)}
            onSetReferent={openReferentForm}
            onRemoveReferent={(contract) => {
              if (isNil(contract.referent)) return;
              setDeleteTarget({
                kind: "referent",
                contractId: contract.id,
                employeeContractId: contract.referent.id,
                label: `le référent ${employeeName(contract.referent)}`,
              });
            }}
            onDeleteHistory={setHistoryRemoval}
            // After mise en service a referent is deactivated and replaced,
            // not removed (PO, N74).
            onDeactivateReferent={
              isCommissioned ? setDeactivatingReferent : undefined
            }
            onInterrupt={setInterruptTarget}
            onDelete={(contract) =>
              setDeleteTarget({
                kind: "contract",
                id: contract.id,
                label: `le contrat ${contract.performer.name}`,
                subContractCount: subContractCount(contract.id),
              })
            }
            canDeleteRang1={yardState === "preparation"}
            canAddContracts={canAddContracts}
            toolbar={
              <ContractGroupsToolbar
                count={groups.length}
                expanded={groupsExpanded}
                onToggle={() => setGroupsExpanded((v) => !v)}
                onCreate={() => setGroupFormOpen(true)}
              />
            }
          />
        </ContractsSlideOver>
      </div>

      {/* Forms */}
      <ContractGroupForm
        open={groupFormOpen}
        onOpenChange={setGroupFormOpen}
        yardId={numericYardId}
        onSuccess={fetchData}
      />

      <PerformancesDrawer
        open={prestasDrawerOpen}
        onOpenChange={setPrestasDrawerOpen}
        yardId={numericYardId}
        inPreparation={yardState === "preparation"}
        onSuccess={() => setMatrixRefreshKey((k) => k + 1)}
      />

      {isNotNil(referentContract) && (
        <WorkContractReferentForm
          open={referentFormOpen}
          onOpenChange={setReferentFormOpen}
          contractId={referentContract.id}
          contractType={referentContract.type}
          performerId={referentContract.performer.id}
          referent={referentContract.referent}
          onSuccess={fetchData}
        />
      )}

      {isNotNil(deactivatingReferent?.referent) && (
        <DeactivateCollaboratorModal
          open
          onOpenChange={(open) => {
            if (!open) setDeactivatingReferent(null);
          }}
          employee={{
            id: deactivatingReferent.referent.employeeId,
            name: employeeName(deactivatingReferent.referent),
          }}
          contractId={deactivatingReferent.id}
          onSuccess={fetchData}
        />
      )}

      {isNotNil(teamsContractId) &&
        (() => {
          const teamsContract = contracts.find((c) => c.id === teamsContractId);
          if (isNil(teamsContract)) return null;
          return (
            <ContractTeamsDrawer
              open
              onOpenChange={(open) => {
                if (!open) setTeamsContractId(null);
              }}
              yardId={numericYardId}
              contractId={teamsContractId}
              contractType={teamsContract.type}
              performerId={teamsContract.performer.id}
              performerName={teamsContract.performer.name}
              teams={teamsContract.teams}
              isCommissioned={isCommissioned}
              teamsEditable
              canAddTeams={canAddContracts}
              onSuccess={fetchData}
            />
          );
        })()}

      <ReferentHistoryDeleteDialog
        removal={historyRemoval}
        onClose={() => setHistoryRemoval(null)}
        onDeleted={fetchData}
      />

      {/* Delete confirmation */}
      <Modal.Root
        open={isNotNil(deleteTarget)}
        onOpenChange={(details) => {
          if (!details.open) setDeleteTarget(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Voulez-vous vraiment supprimer{" "}
                  <span className="font-medium">{deleteTarget?.label}</span> ?
                  Cette action est irréversible.
                </p>
                {deleteTarget?.kind === "contract" &&
                  deleteTarget.subContractCount > 0 && (
                    <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400">
                      Contrats sous-traités (rang 2) supprimés avec lui :{" "}
                      {deleteTarget.subContractCount}.
                    </p>
                  )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteTarget(null)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Interrupt + takeover of its interventions */}
      <ContractInterruptDialog
        yardId={numericYardId}
        contract={interruptTarget}
        contracts={contracts}
        subContractCount={
          isNotNil(interruptTarget) ? subContractCount(interruptTarget.id) : 0
        }
        yardMoaCompany={yardMoaCompany}
        onClose={() => setInterruptTarget(null)}
        onInterrupted={onInterrupted}
      />
    </div>
  );
}
